import { useState, useEffect, useRef } from 'react';

const TurnstileWidget = ({ onVerify, sitekey = '0x4AAAAAABcarM3kVEzVYc6Z' }) => {
  const ref = useRef(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    let isMounted = true;
    
    // Function to render the Turnstile widget
    const renderTurnstile = () => {
      if (!window.turnstile || !ref.current || !isMounted) return;
      
      // Clear any existing widgets first
      while (ref.current.firstChild) {
        ref.current.removeChild(ref.current.firstChild);
      }
      
      try {
        window.turnstile.render(ref.current, {
          sitekey,
          callback: token => {
            if (!isMounted) return;
            
            setIsVerified(true);
            
            // Call the onVerify callback with the token
            onVerify(token);
            console.log("Turnstile verification completed:", token);
            
            // Start the fade-out animation after a slightly longer delay
            setTimeout(() => {
              if (isMounted) {
                setIsVisible(false);
              }
            }, 1500); // Extended from 500ms to 1500ms
          },
          'error-callback': () => {
            if (isMounted) {
              setError('Verification failed. Please try again.');
            }
          }
        });
      } catch (err) {
        if (isMounted) {
          setError('Failed to load verification widget.');
          console.error('Turnstile error:', err);
        }
      }
    };
    
    // Load the Turnstile script if it's not already loaded
    if (!window.turnstile && !document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.id = 'turnstile-script';
      
      script.onload = () => {
        if (isMounted) {
          renderTurnstile();
        }
      };
      
      script.onerror = () => {
        if (isMounted) {
          setError('Failed to load verification service.');
          console.error('Failed to load Turnstile script');
        }
      };
      
      document.body.appendChild(script);
    } else {
      // If script already exists, render widget directly
      renderTurnstile();
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (ref.current) {
        // Clear the container to prevent duplicated widgets
        while (ref.current.firstChild) {
          ref.current.removeChild(ref.current.firstChild);
        }
      }
    };
  }, [onVerify, sitekey]); // Added onVerify and sitekey to dependencies
  
  return (
    <div
      className={`transition-all duration-500 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0 scale-95'}`}
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
        height: isVisible ? 'auto' : '0',
        overflow: 'hidden',
        marginBottom: isVisible ? '1rem' : '0'
      }}
      aria-live="polite"
    >
      <div className="p-4 bg-white rounded-lg shadow-md flex flex-col items-center">
        {!isVerified && !error && (
          <p style={{ color: 'white', marginBottom: '1rem' }} >Please complete the verification below</p>
        )}
        {error && (
          <p className="mb-4 text-red-500" role="alert">{error}</p>
        )}
        <div 
          ref={ref} 
          className="cf-turnstile" 
          aria-label="Cloudflare Turnstile verification widget" 
        />
        {isVerified && (
          <p className="mt-2 text-green-500" role="status">Verification successful!</p>
        )}
      </div>
    </div>
  );
};

export default TurnstileWidget;